// The step from a capability part into Explore: which filter takes a part,
// and the address of Explore with parts picked. Pure functions.
//
// A count on the part view is the total of the search with the same picks
// only where Explore has a filter for every part picked, so an address is
// built for those cases and for no other.

import { ClaimedRole, PartRole } from "../../types/capabilityPartDetail";
import {
  DEFAULT_EXPLORE_STATE,
  encodeExploreState,
} from "../Explore/exploreState";

// The filters of Explore that take a capability part
export type PartFilterName = "capability" | "condition" | "mode";

// The filter that takes a part of a role. The capability tree holds effect
// areas, mission areas, function areas and abilities. An object and a means
// have no filter.
const FILTER_OF_ROLE: Partial<Record<PartRole, PartFilterName>> = {
  ability: "capability",
  function_area: "capability",
  effect_area: "capability",
  mission_area: "capability",
  condition: "condition",
  mode: "mode",
};

// The route of Explore
const EXPLORE_PATH = "/explore";

// A part to pick: its id, and its role, which names its filter
export interface PartPick {
  id: string;
  role: PartRole | ClaimedRole;
}

// The filter of Explore that takes a part of the role; null when none does
export const exploreFilterOf = (
  role: PartRole | ClaimedRole,
): PartFilterName | null => FILTER_OF_ROLE[role] ?? null;

/**
 * The address of Explore with every given part picked.
 *
 * @param picks - The parts to pick, each in the filter of its role
 * @returns The address, or null when the list it leads to would not be the
 *   products that have all the parts: a part has no filter, or two parts
 *   share one filter, where picks are alternatives
 */
export const exploreAddress = (picks: PartPick[]): string | null => {
  const picked: Partial<Record<PartFilterName, string[]>> = {};
  for (const pick of picks) {
    const filter = exploreFilterOf(pick.role);
    if (filter === null || picked[filter]) {
      return null;
    }
    picked[filter] = [pick.id];
  }
  if (picks.length === 0) {
    return null;
  }
  return `${EXPLORE_PATH}?${encodeExploreState({
    ...DEFAULT_EXPLORE_STATE,
    ...picked,
  }).toString()}`;
};
